import { useContext, useEffect, useMemo } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import { UserContext } from '../App';
import { FEATURE_FLAGS } from '../constants/constants';
import useNRLazyQuery from '../graphql/useNRLazyQuery';
import { LIST_ACRS, LIST_ROLES, RBAC_LIST_CURSOR } from '../graphql/queries/rbac';
import { buildDisplayTags, normalizeUserGrants } from './userAccess.utils';

const EMPTY_NODES = [];
const EMPTY_GRANTS = [];

const RBAC_QUERY_OPTIONS = {
  fetchPolicy: 'cache-and-network'
};

/**
 * Loads the current user's role-based access grants from listACRs, enriched with
 * permissions from listRoles (joined by roleId). Shared by org selector and profile access.
 * Gated by settings-users-and-access; remove skip/flag check when flag is retired.
 */
function useCurrentUserAccess(userId) {
  const { idToken } = useContext(UserContext);
  const effectiveUserId = userId ?? idToken?.sub;
  // REMOVE(settings-users-and-access): Drop flag gate; always fetch RBAC when flag is retired.
  const { value: isRbacEnabled } = useFlag(FEATURE_FLAGS.SETTINGS_USERS_AND_ACCESS, false);
  const skip = !effectiveUserId || !isRbacEnabled;

  const [acrNodes, acrsLoading, acrsCalled, fetchAcrs, , acrsError] = useNRLazyQuery({
    query: LIST_ACRS,
    qlObjKey: 'listACRs',
    defaultValue: EMPTY_NODES,
    options: RBAC_QUERY_OPTIONS,
    transformData: data => data?.edges?.map(edge => edge.node) ?? EMPTY_NODES
  });

  const [roleNodes, rolesLoading, rolesCalled, fetchRoles, , rolesError] = useNRLazyQuery({
    query: LIST_ROLES,
    qlObjKey: 'listRoles',
    defaultValue: EMPTY_NODES,
    options: RBAC_QUERY_OPTIONS,
    transformData: data => data?.edges?.map(edge => edge.node) ?? EMPTY_NODES
  });

  useEffect(() => {
    if (skip) return;

    fetchAcrs({
      ...RBAC_QUERY_OPTIONS,
      variables: { userId: effectiveUserId, cursor: RBAC_LIST_CURSOR }
    });
    fetchRoles({
      ...RBAC_QUERY_OPTIONS,
      variables: { cursor: RBAC_LIST_CURSOR }
    });
  }, [skip, effectiveUserId, fetchAcrs, fetchRoles]);

  const grants = useMemo(() => {
    if (skip) return EMPTY_GRANTS;
    return normalizeUserGrants(acrNodes, roleNodes);
  }, [skip, acrNodes, roleNodes]);

  const displayTags = useMemo(() => buildDisplayTags(grants), [grants]);

  const called = acrsCalled && rolesCalled;

  return {
    grants,
    displayTags,
    called: skip ? false : called,
    // Treat the window before the effect fires its fetches as loading so consumers
    // don't flash an empty state on first render.
    loading: !skip && (!called || acrsLoading || rolesLoading),
    error: skip ? undefined : acrsError || rolesError
  };
}

export default useCurrentUserAccess;
